import classNames from 'classnames/bind';
import { VideoProviderType } from 'hooks/useVideoPlayer';
import { t } from 'i18next';
import ReactPlayer from 'react-player';
import { isSafari } from 'utils/browserDetection';

import React, { useMemo, useState } from 'react';

import { Button } from '@thinkific/chalk/Button';
import { LoadingSpinner } from '@thinkific/chalk/LoadingSpinner';
import { Pause, Play, VideoCamera, X } from '@thinkific/chalk/react-icons';

import { Attachment } from 'schema/Post/types';

import styles from './VideoPreview.module.scss';

const cx = classNames.bind(styles);

interface VideoPreviewProps {
  attachment: Attachment;
  onRemove: () => void;
  isLoading?: boolean;
}

const VideoPreview = ({ attachment, onRemove, isLoading = false }: VideoPreviewProps) => {
  const [videoPlaying, setVideoPlaying] = useState(false);

  const videoUrl = attachment.url || '';
  const isOnVideoProxy = attachment.provider === VideoProviderType.VIDEO_PROXY;

  // Determine preload strategy based on browser
  // Safari has issues with preload="metadata", so we use "" for Safari
  // Other browsers get "metadata" to save bandwidth by preloading only video metadata
  const preloadStrategy = useMemo(() => {
    return isSafari() ? '' : 'metadata';
  }, []);

  let player: React.ReactNode = null;

  const handlePlayVideo = () => {
    setVideoPlaying(!videoPlaying);
  };

  // Handle thumbnail display (videoProxyId case)
  if (isOnVideoProxy) {
    player = (
      <div className={cx('thumbnail-wrapper')}>
        <img
          alt="Video thumbnail"
          className={cx('thumbnail')}
          src={attachment.thumbnailUrl || ''}
        />
      </div>
    );
  }
  // Handle direct video URL - show as thumbnail
  else if (videoUrl) {
    player = (
      <ReactPlayer
        controls={false}
        height="100%"
        playing={videoPlaying}
        preload={preloadStrategy}
        src={videoUrl}
        width="100%"
        onEnded={() => setVideoPlaying(false)}
        onError={() => {
          setVideoPlaying(false);
        }}
      />
    );
  }
  return (
    <div
      aria-live="polite"
      className={cx('video-attachment__preview-container')}
      data-qa="video-attachment__preview"
      id="video-attachment-preview"
      role="region"
      title="Video Preview"
    >
      {player}

      {isLoading ? (
        <div className={styles['loading-spinner-button']}>
          <LoadingSpinner data-qa="video-preview__loading-spinner" size="xs" onDark />
        </div>
      ) : (
        <>
          {/* Show play button for direct video URL, VideoCamera icon for videoProxyId */}
          {!isOnVideoProxy ? (
            <Button
              aria-label="Play video"
              className={styles['play-video-button']}
              data-qa="image-preview__play-button"
              emphasis="low"
              icon={videoPlaying ? Pause : Play}
              iconStyle="filled"
              size="xs"
              hiddenLabel
              onClick={handlePlayVideo}
              onDark
            >
              {t('play', 'play')}
            </Button>
          ) : (
            <div className={styles['video-icon']}>
              <VideoCamera className={styles['video-icon']} color="onDark.icon.primary" filled />
            </div>
          )}

          <Button
            aria-label="Remove video"
            className={styles['remove-video-button']}
            data-qa="image-preview__remove-button"
            icon={X}
            size="xs"
            hiddenLabel
            onClick={onRemove}
          >
            {t('Remove', 'Remove')}
          </Button>
        </>
      )}
    </div>
  );
};

export default VideoPreview;
